import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import type { BackorderDto, LocationDto, OrderDto, PurchaseOrderDetailDto, PurchaseOrderDto, ReceiptDto, ReturnDto, SupplierDto, TransferDetailDto, TransferDto, WarehouseDto } from '@thebb/shared';
import { get, post, put } from '../api';
import { useAuth } from '../auth';
import { Badge, ErrorBox, Modal, PageTitle, Spinner, fmtTime } from '../components/ui';

export const poStatusLabel: Record<string, string> = { draft: 'Kladde', sent: 'Sendt', confirmed: 'Bekræftet', partially_received: 'Delvist modtaget', received: 'Modtaget', cancelled: 'Annulleret' };
const poTone = (s: string) => (s === 'received' ? 'green' : s === 'cancelled' ? 'red' : s === 'draft' ? 'zinc' : 'blue') as 'green' | 'red' | 'zinc' | 'blue';
export const trStatusLabel: Record<string, string> = { requested: 'Anmodet', called: 'Kaldt hjem', in_transit: 'Undervejs', received: 'Modtaget', cancelled: 'Annulleret' };
const trTone = (s: string) => (s === 'received' ? 'green' : s === 'cancelled' ? 'red' : s === 'in_transit' ? 'amber' : 'blue') as 'green' | 'red' | 'amber' | 'blue';
export const rtStatusLabel: Record<string, string> = { open: 'Åben', labelled: 'Label sendt', received: 'Modtaget', closed: 'Lukket', cancelled: 'Annulleret' };
const rtTone = (s: string) => (s === 'closed' || s === 'received' ? 'green' : s === 'cancelled' ? 'red' : 'blue') as 'green' | 'red' | 'blue';
const kr = (m: number | null | undefined, c = 'DKK') => (m == null ? '' : `${(m / 100).toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${c}`);

function useWarehouses() {
  return useQuery({ queryKey: ['warehouses'], queryFn: () => get<WarehouseDto[]>('/warehouses') });
}

/** Lines typed as "SKU antal [stk.pris]" per line, the fastest way to key a purchase order or transfer. */
function parseLines(text: string, withCost: boolean) {
  const out: { sku: string; quantity: number; unitCostMinor?: number | null }[] = [];
  const errors: string[] = [];
  text.split(/\r?\n/).forEach((raw, i) => {
    const line = raw.trim();
    if (!line) return;
    const parts = line.split(/[\s;\t]+/);
    const sku = parts[0]!;
    const qty = Number(parts[1]);
    if (!Number.isInteger(qty) || qty <= 0) { errors.push(`Linje ${i + 1}: antal mangler`); return; }
    const cost = withCost && parts[2] ? Math.round(Number(parts[2].replace(/\./g, '').replace(',', '.')) * 100) : null;
    out.push(withCost ? { sku, quantity: qty, unitCostMinor: cost != null && !Number.isNaN(cost) ? cost : null } : { sku, quantity: qty });
  });
  return { lines: out, errors };
}

/** A location in one warehouse, typed or scanned by code. */
export function LocationInput({ warehouseId, value, onChange, placeholder }: { warehouseId: string; value: string; onChange: (id: string, code: string) => void; placeholder?: string }) {
  const [code, setCode] = useState('');
  const list = useQuery({ queryKey: ['locations', 'pick', warehouseId, code], queryFn: () => get<{ items: LocationDto[] }>(`/locations?warehouseId=${warehouseId}&perPage=20${code ? `&q=${encodeURIComponent(code)}` : ''}`), enabled: !!warehouseId });
  const exact = list.data?.items.find((l) => l.code.toUpperCase() === code.trim().toUpperCase() || l.barcode === code.trim());
  useEffect(() => {
    if (exact && exact.id !== value) onChange(exact.id, exact.code);
  }, [exact, value, onChange]);
  const id = `loc-${warehouseId}`;
  return (
    <span className="inline-flex items-center gap-1">
      <input className={`input w-28 ${code && !exact ? 'border-danger' : ''}`} list={id} value={code} placeholder={placeholder ?? 'Lokation'} onChange={(e) => { setCode(e.target.value); if (!e.target.value) onChange('', ''); }} />
      <datalist id={id}>{list.data?.items.map((l) => <option key={l.id} value={l.code}>{l.type}</option>)}</datalist>
    </span>
  );
}

// --- purchasing ------------------------------------------------------------------------------------------

export function PurchasingPage() {
  const { can } = useAuth();
  const [tab, setTab] = useState<'orders' | 'suppliers' | 'receipts'>('orders');
  const [error, setError] = useState<unknown>(null);
  return (
    <div className="space-y-4">
      <PageTitle title="Indkøb" subtitle="Indkøbsordrer til leverandører, modtagelse med placering, og det der er på vej" />
      <div className="flex gap-1 border-b border-line">
        {([['orders', 'Indkøbsordrer'], ['suppliers', 'Leverandører'], ['receipts', 'Modtagelser']] as const).map(([k, l]) => (
          <button key={k} className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${tab === k ? 'border-primary text-primary' : 'border-transparent text-muted hover:text-ink'}`} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>
      <ErrorBox error={error} />
      {tab === 'orders' && <PurchaseOrders onError={setError} canEdit={can('purchasing.edit')} />}
      {tab === 'suppliers' && <Suppliers onError={setError} canEdit={can('purchasing.edit')} />}
      {tab === 'receipts' && <Receipts />}
    </div>
  );
}

function PurchaseOrders({ onError, canEdit }: { onError: (e: unknown) => void; canEdit: boolean }) {
  const [status, setStatus] = useState('open');
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const params = useMemo(() => { const p = new URLSearchParams({ perPage: '100' }); if (status === 'open') p.set('open', 'true'); else if (status) p.set('status', status); if (q) p.set('q', q); return p.toString(); }, [status, q]);
  const list = useQuery({ queryKey: ['pos', params], queryFn: () => get<{ items: PurchaseOrderDto[]; total: number }>(`/purchase-orders?${params}`) });
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <input className="input w-60" placeholder="Søg nr., leverandør, reference" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="input w-48" value={status} onChange={(e) => setStatus(e.target.value)}><option value="open">Åbne</option><option value="">Alle</option>{Object.entries(poStatusLabel).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
        {canEdit && <button className="btn btn-primary ml-auto" onClick={() => setCreating(true)}>Ny indkøbsordre</button>}
      </div>
      {list.isLoading ? <Spinner /> : (
        <table className="table card p-0">
          <thead><tr><th>Nr.</th><th>Leverandør</th><th>Lager</th><th>Status</th><th>Forventet</th><th className="text-right">Linjer</th><th className="text-right">Bestilt</th><th className="text-right">Modtaget</th><th className="text-right">Værdi</th><th>Oprettet</th></tr></thead>
          <tbody>
            {list.data?.items.map((p) => (
              <tr key={p.id}>
                <td><Link to="/purchasing/$id" params={{ id: p.id }} className="font-medium text-primary hover:underline">{p.number}</Link>{p.reference && <div className="text-xs text-muted">{p.reference}</div>}</td>
                <td>{p.supplierName}</td>
                <td>{p.warehouseCode}</td>
                <td><Badge tone={poTone(p.status)}>{poStatusLabel[p.status]}</Badge></td>
                <td>{p.expectedAt ?? ''}</td>
                <td className="text-right tabular-nums">{p.lineCount}</td>
                <td className="text-right tabular-nums">{p.quantityOrdered}</td>
                <td className="text-right tabular-nums">{p.quantityReceived}</td>
                <td className="text-right tabular-nums">{p.totalCostMinor ? kr(p.totalCostMinor, p.currency) : ''}</td>
                <td className="whitespace-nowrap text-xs text-muted">{p.createdBy} · {fmtTime(p.createdAt)}</td>
              </tr>
            ))}
            {list.data?.items.length === 0 && <tr><td colSpan={10} className="text-muted">Ingen indkøbsordrer.</td></tr>}
          </tbody>
        </table>
      )}
      {creating && <PoEditor onClose={() => setCreating(false)} onError={onError} />}
    </div>
  );
}

function PoEditor({ po, onClose, onError }: { po?: PurchaseOrderDetailDto; onClose: () => void; onError: (e: unknown) => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const suppliers = useQuery({ queryKey: ['suppliers'], queryFn: () => get<SupplierDto[]>('/suppliers') });
  const warehouses = useWarehouses();
  const [form, setForm] = useState({
    supplierId: po?.supplierId ?? '',
    warehouseId: po?.warehouseId ?? '',
    expectedAt: po?.expectedAt ?? '',
    reference: po?.reference ?? '',
    note: po?.note ?? '',
    lines: po?.lines.map((l) => `${l.sku} ${l.quantityOrdered}${l.unitCostMinor != null ? ` ${(l.unitCostMinor / 100).toFixed(2).replace('.', ',')}` : ''}`).join('\n') ?? '',
  });
  useEffect(() => {
    if (!form.warehouseId && warehouses.data?.length) setForm((f) => ({ ...f, warehouseId: warehouses.data!.find((w) => w.isDefaultReceiving)?.id ?? warehouses.data![0]!.id }));
  }, [warehouses.data, form.warehouseId]);
  const parsed = parseLines(form.lines, true);
  const save = useMutation({
    mutationFn: () => {
      const body = { supplierId: form.supplierId, warehouseId: form.warehouseId, expectedAt: form.expectedAt || null, reference: form.reference || null, note: form.note || null, lines: parsed.lines };
      return po ? put<PurchaseOrderDetailDto>(`/purchase-orders/${po.id}`, body) : post<PurchaseOrderDetailDto>('/purchase-orders', body);
    },
    onSuccess: (r) => { void qc.invalidateQueries({ queryKey: ['pos'] }); void qc.invalidateQueries({ queryKey: ['po', r.id] }); onClose(); if (!po) void navigate({ to: '/purchasing/$id', params: { id: r.id } }); },
    onError,
  });
  return (
    <Modal title={po ? `Ret ${po.number}` : 'Ny indkøbsordre'} onClose={onClose}>
      <div className="grid gap-3 text-sm sm:grid-cols-2">
        <div><label className="label">Leverandør</label><select className="input" value={form.supplierId} onChange={(e) => setForm({ ...form, supplierId: e.target.value })}><option value="">Vælg</option>{suppliers.data?.filter((s) => s.active).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
        <div><label className="label">Leveres til lager</label><select className="input" value={form.warehouseId} onChange={(e) => setForm({ ...form, warehouseId: e.target.value })}>{warehouses.data?.filter((w) => w.active).map((w) => <option key={w.id} value={w.id}>{w.code} · {w.name}</option>)}</select></div>
        <div><label className="label">Forventet levering</label><input className="input" type="date" value={form.expectedAt} onChange={(e) => setForm({ ...form, expectedAt: e.target.value })} /></div>
        <div><label className="label">Leverandørens reference</label><input className="input" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></div>
        <div className="sm:col-span-2"><label className="label">Linjer: varenummer, antal og evt. stk.pris, én pr. linje</label><textarea className="input font-mono" rows={8} value={form.lines} onChange={(e) => setForm({ ...form, lines: e.target.value })} placeholder={'BG267129 24 312,50\nNL261.S 100'} /></div>
        {parsed.errors.length > 0 && <div className="text-red-700 sm:col-span-2">{parsed.errors.join('. ')}</div>}
        <div className="sm:col-span-2"><label className="label">Note til leverandøren</label><textarea className="input" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
      </div>
      <div className="mt-4 flex justify-end gap-2"><button className="btn" onClick={onClose}>Annullér</button><button className="btn btn-primary" disabled={save.isPending || !form.supplierId || !parsed.lines.length || parsed.errors.length > 0} onClick={() => save.mutate()}>Gem ({parsed.lines.length} linjer)</button></div>
    </Modal>
  );
}

export function PurchaseOrderPage() {
  const { id } = useParams({ from: '/app/purchasing/$id' });
  const qc = useQueryClient();
  const { can } = useAuth();
  const [error, setError] = useState<unknown>(null);
  const [editing, setEditing] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const po = useQuery({ queryKey: ['po', id], queryFn: () => get<PurchaseOrderDetailDto>(`/purchase-orders/${id}`) });
  const set = (r: PurchaseOrderDetailDto) => { qc.setQueryData(['po', id], r); void qc.invalidateQueries({ queryKey: ['pos'] }); setError(null); };
  const send = useMutation({ mutationFn: () => post<PurchaseOrderDetailDto & { documentId: string; mailed: boolean }>(`/purchase-orders/${id}/send`, { email: true }), onSuccess: (r) => { set(r); window.open(`/api/documents/${r.documentId}/download`, '_blank'); setMsg(r.mailed ? `Sendt til ${r.supplierEmail}` : 'PDF lavet; leverandøren har ingen mailadresse'); }, onError: setError });
  const [confirmForm, setConfirmForm] = useState({ reference: '', expectedAt: '' });
  const confirm = useMutation({ mutationFn: () => post<PurchaseOrderDetailDto>(`/purchase-orders/${id}/confirm`, { reference: confirmForm.reference || null, expectedAt: confirmForm.expectedAt || null }), onSuccess: set, onError: setError });
  const cancel = useMutation({ mutationFn: () => post<PurchaseOrderDetailDto>(`/purchase-orders/${id}/cancel`), onSuccess: set, onError: setError });
  const p = po.data;
  if (!p) return <Spinner />;
  const open = ['draft', 'sent', 'confirmed', 'partially_received'].includes(p.status);
  return (
    <div className="space-y-4">
      <PageTitle
        title={`Indkøbsordre ${p.number}`}
        subtitle={`${p.supplierName} · til ${p.warehouseCode}${p.expectedAt ? ` · forventet ${p.expectedAt}` : ''}`}
        actions={
          <>
            <Link to="/purchasing" className="btn">Tilbage</Link>
            {can('purchasing.edit') && ['draft', 'sent', 'confirmed'].includes(p.status) && p.quantityReceived === 0 && <button className="btn" onClick={() => setEditing(true)}>Ret</button>}
            {can('purchasing.edit') && ['draft', 'sent'].includes(p.status) && <button className="btn btn-primary" disabled={send.isPending} onClick={() => send.mutate()}>{p.status === 'draft' ? 'Send til leverandør' : 'Send igen'}</button>}
            {can('purchasing.edit') && open && <button className="btn" disabled={cancel.isPending} onClick={() => window.confirm(p.status === 'partially_received' ? 'Luk ordren? Resten forventes ikke længere.' : 'Annullér ordren?') && cancel.mutate()}>{p.status === 'partially_received' ? 'Luk' : 'Annullér'}</button>}
          </>
        }
      />
      <ErrorBox error={error} />
      {msg && <div className="text-sm text-success">{msg}</div>}
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <Badge tone={poTone(p.status)}>{poStatusLabel[p.status]}</Badge>
        {p.reference && <span>Reference {p.reference}</span>}
        <span className="text-muted">Oprettet af {p.createdBy} {fmtTime(p.createdAt)}{p.sentAt ? ` · sendt ${fmtTime(p.sentAt)}` : ''}</span>
      </div>
      {can('purchasing.edit') && ['sent', 'draft'].includes(p.status) && (
        <div className="card flex flex-wrap items-end gap-2 text-sm">
          <div><label className="label">Leverandørens ordrebekræftelse</label><input className="input w-48" value={confirmForm.reference} onChange={(e) => setConfirmForm({ ...confirmForm, reference: e.target.value })} placeholder="Reference" /></div>
          <div><label className="label">Bekræftet levering</label><input className="input" type="date" value={confirmForm.expectedAt} onChange={(e) => setConfirmForm({ ...confirmForm, expectedAt: e.target.value })} /></div>
          <button className="btn" disabled={confirm.isPending} onClick={() => confirm.mutate()}>Markér bekræftet</button>
        </div>
      )}
      {open && can('stock.receive') ? <ReceivePanel kind="purchase-orders" id={p.id} warehouseId={p.warehouseId} lines={p.lines.map((l) => ({ id: l.id, sku: l.sku, title: l.title, expected: l.quantityOrdered, received: l.quantityReceived, extra: l.unitCostMinor != null ? kr(l.unitCostMinor, p.currency) : '' }))} onDone={(r) => { set(r as PurchaseOrderDetailDto); }} onError={setError} /> : (
        <table className="table card p-0">
          <thead><tr><th>Varenr.</th><th>Vare</th><th className="text-right">Bestilt</th><th className="text-right">Modtaget</th><th className="text-right">Stk.pris</th></tr></thead>
          <tbody>{p.lines.map((l) => <tr key={l.id}><td>{l.sku}</td><td>{l.title}</td><td className="text-right tabular-nums">{l.quantityOrdered}</td><td className="text-right tabular-nums">{l.quantityReceived}</td><td className="text-right tabular-nums">{kr(l.unitCostMinor, p.currency)}</td></tr>)}</tbody>
        </table>
      )}
      <ReceiptList receipts={p.receipts} />
      {editing && <PoEditor po={p} onClose={() => setEditing(false)} onError={setError} />}
    </div>
  );
}

/** Receiving at a desk: quantity and put-away location per line, one receipt. */
function ReceivePanel({ kind, id, warehouseId, lines, onDone, onError }: { kind: 'purchase-orders' | 'transfers'; id: string; warehouseId: string; lines: { id: string; sku: string; title: string; expected: number; received: number; extra?: string }[]; onDone: (r: unknown) => void; onError: (e: unknown) => void }) {
  const [qty, setQty] = useState<Record<string, string>>({});
  const [loc, setLoc] = useState<Record<string, string>>({});
  const [allLoc, setAllLoc] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const receive = useMutation({
    mutationFn: () => post<{ receiptNumber: string; releasedFulfilments: string[]; overReceived?: boolean }>(`/${kind}/${id}/receive`, {
      lines: lines.filter((l) => Number(qty[l.id]) > 0).map((l) => ({ lineId: l.id, quantity: Number(qty[l.id]), locationId: loc[l.id] || allLoc })),
    }),
    onSuccess: (r) => { onDone(r); setQty({}); setResult(`${r.receiptNumber} bogført${r.overReceived ? ' (mere end bestilt)' : ''}${r.releasedFulfilments.length ? `. Restordrer frigivet: ${r.releasedFulfilments.join(', ')}` : ''}`); },
    onError,
  });
  const chosen = lines.filter((l) => Number(qty[l.id]) > 0);
  const missingLoc = chosen.some((l) => !(loc[l.id] || allLoc));
  return (
    <div className="card space-y-3 p-0">
      <div className="flex flex-wrap items-center gap-2 px-4 pt-4 text-sm">
        <div className="label m-0">Modtag varer</div>
        <span className="text-muted">Læg alt på</span>
        <LocationInput warehouseId={warehouseId} value={allLoc} onChange={(v) => setAllLoc(v)} placeholder="fx MODT" />
        <button className="btn" onClick={() => setQty(Object.fromEntries(lines.map((l) => [l.id, String(Math.max(0, l.expected - l.received))])))}>Udfyld resten</button>
        <button className="btn btn-primary ml-auto" disabled={receive.isPending || !chosen.length || missingLoc} onClick={() => receive.mutate()}>Bogfør modtagelse ({chosen.reduce((t, l) => t + Number(qty[l.id]), 0)} stk.)</button>
      </div>
      {result && <div className="px-4 text-sm text-success">{result}</div>}
      <table className="table">
        <thead><tr><th>Varenr.</th><th>Vare</th><th className="text-right">Forventet</th><th className="text-right">Modtaget</th><th className="text-right">Rest</th><th>Antal nu</th><th>Lokation</th></tr></thead>
        <tbody>
          {lines.map((l) => {
            const rest = l.expected - l.received;
            return (
              <tr key={l.id} className={rest <= 0 ? 'text-muted' : ''}>
                <td>{l.sku}</td>
                <td>{l.title}{l.extra && <span className="text-xs text-muted"> · {l.extra}</span>}</td>
                <td className="text-right tabular-nums">{l.expected}</td>
                <td className="text-right tabular-nums">{l.received}</td>
                <td className="text-right tabular-nums">{Math.max(0, rest)}</td>
                <td><input className="input w-20" type="number" min={0} value={qty[l.id] ?? ''} onChange={(e) => setQty({ ...qty, [l.id]: e.target.value })} /></td>
                <td><LocationInput warehouseId={warehouseId} value={loc[l.id] ?? ''} onChange={(v) => setLoc((x) => ({ ...x, [l.id]: v }))} placeholder={allLoc ? 'som ovenfor' : 'Lokation'} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ReceiptList({ receipts }: { receipts: ReceiptDto[] }) {
  if (!receipts.length) return null;
  return (
    <section>
      <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted">Modtagelser</h2>
      <div className="space-y-2">
        {receipts.map((r) => (
          <div key={r.id} className="card text-sm">
            <div className="font-medium">{r.number} <span className="font-normal text-muted">· {r.receivedBy} · {fmtTime(r.receivedAt)}{r.sourceNumber ? ` · ${r.sourceNumber}` : ''}</span></div>
            <div className="text-muted">{r.lines.map((l) => `${l.quantity} × ${l.sku}${l.locationCode ? ` → ${l.locationCode}` : ''}${l.disposition === 'scrap' ? ' (kasseret)' : ''}`).join(' · ')}</div>
            {r.note && <div>{r.note}</div>}
          </div>
        ))}
      </div>
    </section>
  );
}

function Receipts() {
  const list = useQuery({ queryKey: ['receipts'], queryFn: () => get<ReceiptDto[]>('/receipts') });
  if (!list.data) return <Spinner />;
  if (!list.data.length) return <div className="text-sm text-muted">Ingen modtagelser endnu.</div>;
  return <ReceiptList receipts={list.data} />;
}

function Suppliers({ onError, canEdit }: { onError: (e: unknown) => void; canEdit: boolean }) {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['suppliers'], queryFn: () => get<SupplierDto[]>('/suppliers') });
  const [editing, setEditing] = useState<SupplierDto | 'new' | null>(null);
  return (
    <div className="space-y-3">
      <table className="table card p-0">
        <thead><tr><th>Navn</th><th>Kontakt</th><th>Mail</th><th>Telefon</th><th className="text-right">Leveringstid</th><th className="text-right">Åbne ordrer</th><th></th></tr></thead>
        <tbody>
          {list.data?.map((s) => (
            <tr key={s.id} className={s.active ? '' : 'text-muted'}>
              <td className="font-medium">{s.name}</td><td>{s.contact}</td><td>{s.email}</td><td>{s.phone}</td>
              <td className="text-right">{s.leadTimeDays != null ? `${s.leadTimeDays} dage` : ''}</td>
              <td className="text-right tabular-nums">{s.openOrders}</td>
              <td className="text-right">{canEdit && <button className="btn" onClick={() => setEditing(s)}>Ret</button>}</td>
            </tr>
          ))}
          {list.data?.length === 0 && <tr><td colSpan={7} className="text-muted">Ingen leverandører endnu.</td></tr>}
        </tbody>
      </table>
      {canEdit && <button className="btn btn-primary" onClick={() => setEditing('new')}>Ny leverandør</button>}
      {editing && <SupplierModal supplier={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={(r) => { qc.setQueryData(['suppliers'], r); setEditing(null); }} onError={onError} />}
    </div>
  );
}

function SupplierModal({ supplier, onClose, onSaved, onError }: { supplier: SupplierDto | null; onClose: () => void; onSaved: (r: SupplierDto[]) => void; onError: (e: unknown) => void }) {
  const [f, setF] = useState({ name: supplier?.name ?? '', contact: supplier?.contact ?? '', email: supplier?.email ?? '', phone: supplier?.phone ?? '', leadTimeDays: supplier?.leadTimeDays != null ? String(supplier.leadTimeDays) : '', currency: supplier?.currency ?? 'DKK', note: supplier?.note ?? '', active: supplier?.active ?? true });
  const save = useMutation({
    mutationFn: () => { const body = { ...f, leadTimeDays: f.leadTimeDays ? Number(f.leadTimeDays) : null, contact: f.contact || null, phone: f.phone || null, note: f.note || null }; return supplier ? put<SupplierDto[]>(`/suppliers/${supplier.id}`, body) : post<SupplierDto[]>('/suppliers', body); },
    onSuccess: onSaved,
    onError,
  });
  const field = (k: keyof typeof f, label: string, props: Record<string, unknown> = {}) => <div><label className="label">{label}</label><input className="input" value={String(f[k])} onChange={(e) => setF({ ...f, [k]: e.target.value })} {...props} /></div>;
  return (
    <Modal title={supplier ? supplier.name : 'Ny leverandør'} onClose={onClose}>
      <div className="grid gap-3 text-sm sm:grid-cols-2">
        {field('name', 'Navn')}{field('contact', 'Kontaktperson')}{field('email', 'Mail (indkøbsordrer sendes hertil)', { type: 'email' })}{field('phone', 'Telefon')}{field('leadTimeDays', 'Leveringstid (dage)', { type: 'number' })}{field('currency', 'Valuta')}
        <div className="sm:col-span-2"><label className="label">Note</label><textarea className="input" rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></div>
        <label className="flex items-center gap-2"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Aktiv</label>
      </div>
      <div className="mt-4 flex justify-end gap-2"><button className="btn" onClick={onClose}>Annullér</button><button className="btn btn-primary" disabled={save.isPending || !f.name} onClick={() => save.mutate()}>Gem</button></div>
    </Modal>
  );
}

// --- transfers ---------------------------------------------------------------------------------------------

export function TransfersPage() {
  const { can } = useAuth();
  const [error, setError] = useState<unknown>(null);
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState(true);
  const list = useQuery({ queryKey: ['transfers', open], queryFn: () => get<{ items: TransferDto[] }>(`/transfers?perPage=100${open ? '&open=true' : ''}`) });
  return (
    <div className="space-y-4">
      <PageTitle title="Overførsler" subtitle="Varer kaldt hjem fra fjernlager (FLP) eller flyttet mellem lagre" actions={can('stock.transfer') && <button className="btn btn-primary" onClick={() => setCreating(true)}>Kald varer hjem</button>} />
      <ErrorBox error={error} />
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={open} onChange={(e) => setOpen(e.target.checked)} /> Kun åbne</label>
      <table className="table card p-0">
        <thead><tr><th>Nr.</th><th>Fra</th><th>Til</th><th>Status</th><th>Paller</th><th>Forventet</th><th className="text-right">Linjer</th><th className="text-right">Antal</th><th>Oprettet</th></tr></thead>
        <tbody>
          {list.data?.items.map((t) => (
            <tr key={t.id}>
              <td><Link to="/transfers/$id" params={{ id: t.id }} className="font-medium text-primary hover:underline">{t.number}</Link></td>
              <td>{t.fromWarehouseCode}</td><td>{t.toWarehouseCode}</td>
              <td><Badge tone={trTone(t.status)}>{trStatusLabel[t.status]}</Badge></td>
              <td>{t.requestedPallets}</td><td>{t.expectedAt}</td>
              <td className="text-right tabular-nums">{t.lineCount}</td><td className="text-right tabular-nums">{t.quantityRequested}</td>
              <td className="whitespace-nowrap text-xs text-muted">{t.createdBy} · {fmtTime(t.createdAt)}</td>
            </tr>
          ))}
          {list.data?.items.length === 0 && <tr><td colSpan={9} className="text-muted">Ingen overførsler.</td></tr>}
        </tbody>
      </table>
      {creating && <TransferEditor onClose={() => setCreating(false)} onError={setError} />}
    </div>
  );
}

function TransferEditor({ onClose, onError }: { onClose: () => void; onError: (e: unknown) => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const warehouses = useWarehouses();
  const [f, setF] = useState({ fromWarehouseId: '', toWarehouseId: '', expectedAt: '', requestedPallets: '', note: '', lines: '' });
  useEffect(() => {
    if (warehouses.data && !f.fromWarehouseId) setF((x) => ({ ...x, fromWarehouseId: warehouses.data!.find((w) => w.type === 'remote')?.id ?? '', toWarehouseId: warehouses.data!.find((w) => w.isDefaultReceiving)?.id ?? warehouses.data!.find((w) => w.type === 'operated')?.id ?? '' }));
  }, [warehouses.data, f.fromWarehouseId]);
  const parsed = parseLines(f.lines, false);
  const save = useMutation({
    mutationFn: () => post<TransferDetailDto>('/transfers', { fromWarehouseId: f.fromWarehouseId, toWarehouseId: f.toWarehouseId, expectedAt: f.expectedAt || null, requestedPallets: f.requestedPallets || null, note: f.note || null, lines: parsed.lines }),
    onSuccess: (r) => { void qc.invalidateQueries({ queryKey: ['transfers'] }); onClose(); void navigate({ to: '/transfers/$id', params: { id: r.id } }); },
    onError,
  });
  return (
    <Modal title="Kald varer hjem" onClose={onClose}>
      <div className="grid gap-3 text-sm sm:grid-cols-2">
        <div><label className="label">Fra lager</label><select className="input" value={f.fromWarehouseId} onChange={(e) => setF({ ...f, fromWarehouseId: e.target.value })}><option value="">Vælg</option>{warehouses.data?.map((w) => <option key={w.id} value={w.id}>{w.code} · {w.name}</option>)}</select></div>
        <div><label className="label">Til lager</label><select className="input" value={f.toWarehouseId} onChange={(e) => setF({ ...f, toWarehouseId: e.target.value })}><option value="">Vælg</option>{warehouses.data?.filter((w) => w.type === 'operated').map((w) => <option key={w.id} value={w.id}>{w.code} · {w.name}</option>)}</select></div>
        <div><label className="label">Forventet</label><input className="input" type="date" value={f.expectedAt} onChange={(e) => setF({ ...f, expectedAt: e.target.value })} /></div>
        <div><label className="label">Paller</label><input className="input" value={f.requestedPallets} onChange={(e) => setF({ ...f, requestedPallets: e.target.value })} placeholder="fx 3 hele, 1 halv" /></div>
        <div className="sm:col-span-2"><label className="label">Linjer: varenummer og antal, én pr. linje</label><textarea className="input font-mono" rows={8} value={f.lines} onChange={(e) => setF({ ...f, lines: e.target.value })} placeholder={'BG267129 240\nNL261.S 1200'} /></div>
        {parsed.errors.length > 0 && <div className="text-red-700 sm:col-span-2">{parsed.errors.join('. ')}</div>}
        <div className="sm:col-span-2"><label className="label">Note</label><textarea className="input" rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></div>
      </div>
      <div className="mt-4 flex justify-end gap-2"><button className="btn" onClick={onClose}>Annullér</button><button className="btn btn-primary" disabled={save.isPending || !f.fromWarehouseId || !f.toWarehouseId || !parsed.lines.length || parsed.errors.length > 0} onClick={() => save.mutate()}>Opret ({parsed.lines.length} linjer)</button></div>
    </Modal>
  );
}

export function TransferPage() {
  const { id } = useParams({ from: '/app/transfers/$id' });
  const qc = useQueryClient();
  const { can } = useAuth();
  const [error, setError] = useState<unknown>(null);
  const [expected, setExpected] = useState('');
  const [warn, setWarn] = useState<string[]>([]);
  const t = useQuery({ queryKey: ['transfer', id], queryFn: () => get<TransferDetailDto>(`/transfers/${id}`) });
  const set = (r: TransferDetailDto) => { qc.setQueryData(['transfer', id], r); void qc.invalidateQueries({ queryKey: ['transfers'] }); setError(null); };
  const call = useMutation({ mutationFn: () => post<TransferDetailDto>(`/transfers/${id}/call`, { expectedAt: expected || null }), onSuccess: set, onError: setError });
  const ship = useMutation({ mutationFn: () => post<TransferDetailDto & { warnings: string[] }>(`/transfers/${id}/ship`, {}), onSuccess: (r) => { set(r); setWarn(r.warnings); }, onError: setError });
  const cancel = useMutation({ mutationFn: () => post<TransferDetailDto>(`/transfers/${id}/cancel`), onSuccess: set, onError: setError });
  const d = t.data;
  if (!d) return <Spinner />;
  const manage = can('stock.transfer');
  return (
    <div className="space-y-4">
      <PageTitle
        title={`Overførsel ${d.number}`}
        subtitle={`${d.fromWarehouseCode} → ${d.toWarehouseCode}${d.requestedPallets ? ` · ${d.requestedPallets}` : ''}${d.expectedAt ? ` · forventet ${d.expectedAt}` : ''}`}
        actions={
          <>
            <Link to="/transfers" className="btn">Tilbage</Link>
            {manage && ['requested', 'called'].includes(d.status) && <button className="btn" disabled={ship.isPending} onClick={() => window.confirm(`Markér ${d.number} som afsendt fra ${d.fromWarehouseCode}? Varerne trækkes fra ${d.fromWarehouseCode}.`) && ship.mutate()}>Markér afsendt</button>}
            {manage && ['requested', 'called'].includes(d.status) && <button className="btn" disabled={cancel.isPending} onClick={() => window.confirm('Annullér overførslen?') && cancel.mutate()}>Annullér</button>}
          </>
        }
      />
      <ErrorBox error={error} />
      {warn.length > 0 && <div className="rounded-lg bg-amber-50 p-3 text-sm">{d.fromWarehouseCode} havde ikke alt i systemet: {warn.join('; ')}. Tæl {d.fromWarehouseCode} op, så tallene passer.</div>}
      <div className="flex flex-wrap items-center gap-3 text-sm"><Badge tone={trTone(d.status)}>{trStatusLabel[d.status]}</Badge><span className="text-muted">{d.createdBy} · {fmtTime(d.createdAt)}{d.shippedAt ? ` · afsendt ${fmtTime(d.shippedAt)}` : ''}</span>{d.note && <span>{d.note}</span>}</div>
      {manage && d.status === 'requested' && (
        <div className="card flex flex-wrap items-end gap-2 text-sm">
          <div><label className="label">Forventet ankomst</label><input className="input" type="date" value={expected} onChange={(e) => setExpected(e.target.value)} /></div>
          <button className="btn btn-primary" disabled={call.isPending} onClick={() => call.mutate()}>Markér kaldt hjem</button>
          <span className="text-muted">Når fjernlageret er bedt om at sende varerne. Restordrer viser så "kaldt hjem".</span>
        </div>
      )}
      {['requested', 'called', 'in_transit'].includes(d.status) && can('stock.receive') ? (
        <ReceivePanel kind="transfers" id={d.id} warehouseId={d.toWarehouseId} lines={d.lines.map((l) => ({ id: l.id, sku: l.sku, title: l.title, expected: Math.max(l.quantityShipped, l.quantityRequested), received: l.quantityReceived, extra: `${d.fromWarehouseCode}: ${l.availableAtSource}` }))} onDone={(r) => set(r as TransferDetailDto)} onError={setError} />
      ) : (
        <table className="table card p-0">
          <thead><tr><th>Varenr.</th><th>Vare</th><th className="text-right">Anmodet</th><th className="text-right">Sendt</th><th className="text-right">Modtaget</th></tr></thead>
          <tbody>{d.lines.map((l) => <tr key={l.id}><td>{l.sku}</td><td>{l.title}</td><td className="text-right tabular-nums">{l.quantityRequested}</td><td className="text-right tabular-nums">{l.quantityShipped}</td><td className="text-right tabular-nums">{l.quantityReceived}</td></tr>)}</tbody>
        </table>
      )}
      <ReceiptList receipts={d.receipts} />
    </div>
  );
}

// --- backorders ---------------------------------------------------------------------------------------------

export function BackordersPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [q, setQ] = useState('');
  const [coverable, setCoverable] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const list = useQuery({ queryKey: ['backorders', q, coverable], queryFn: () => get<BackorderDto[]>(`/backorders?${new URLSearchParams({ ...(q ? { q } : {}), ...(coverable ? { coverable: 'true' } : {}) })}`) });
  const release = useMutation({ mutationFn: () => post<{ released: string[] }>('/backorders/release', {}), onSuccess: (r) => { setMsg(r.released.length ? `Frigivet: ${r.released.join(', ')}` : 'Intet kunne frigives'); void qc.invalidateQueries({ queryKey: ['backorders'] }); }, onError: setError });
  const rows = list.data ?? [];
  const units = rows.reduce((t, r) => t + r.backorder, 0);
  return (
    <div className="space-y-4">
      <PageTitle title="Restordrer" subtitle="Ordrelinjer der venter på varer, med hvad der er på vej" actions={can('orders.release') && <button className="btn btn-primary" disabled={release.isPending} onClick={() => release.mutate()}>Frigiv det der kan dækkes</button>} />
      <ErrorBox error={error} />
      {msg && <div className="text-sm text-success">{msg}</div>}
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <input className="input w-60" placeholder="Søg SKU, vare, ordrenr." value={q} onChange={(e) => setQ(e.target.value)} />
        <label className="flex items-center gap-2"><input type="checkbox" checked={coverable} onChange={(e) => setCoverable(e.target.checked)} /> Kun dem der kan dækkes nu</label>
        <span className="text-muted">{rows.length} linjer · {units} stk.</span>
      </div>
      {list.isLoading ? <Spinner /> : (
        <table className="table card p-0">
          <thead><tr><th>Ordre</th><th>Shop</th><th>Bestilt</th><th>SKU</th><th>Vare</th><th className="text-right">Mangler</th><th className="text-right">Ledigt nu</th><th>På vej</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.orderLineId} className={r.available >= r.backorder ? 'bg-green-50' : ''}>
                <td><Link to="/orders/$id" params={{ id: r.orderId }} className="text-primary hover:underline">{r.orderNumber}</Link></td>
                <td className="text-xs">{r.shopName}</td>
                <td className="whitespace-nowrap text-xs text-muted">{fmtTime(r.placedAt)}</td>
                <td>{r.sku}</td>
                <td>{r.title}</td>
                <td className="text-right font-semibold tabular-nums">{r.backorder}</td>
                <td className="text-right tabular-nums">{r.available}</td>
                <td className="text-xs">{r.incoming.length ? r.incoming.map((i) => `${i.quantity} stk. ${i.expectedAt ?? 'uden dato'} (${i.source})`).join(', ') : <span className="text-red-700">intet bestilt</span>}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="text-muted">Ingen restordrer.</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );
}

// --- returns -------------------------------------------------------------------------------------------------

export function ReturnsPage() {
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const list = useQuery({ queryKey: ['returns', status, q], queryFn: () => get<ReturnDto[]>(`/returns?${new URLSearchParams({ ...(status ? { status } : {}), ...(q ? { q } : {}) })}`) });
  return (
    <div className="space-y-4">
      <PageTitle title="Returer" subtitle="Opret en retur fra ordren. Returlabel, modtagelse, på lager eller kassation" />
      <div className="flex flex-wrap gap-2">
        <input className="input w-60" placeholder="Søg nr., ordre, kunde" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="input w-44" value={status} onChange={(e) => setStatus(e.target.value)}><option value="">Alle</option>{Object.entries(rtStatusLabel).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
      </div>
      <table className="table card p-0">
        <thead><tr><th>Nr.</th><th>Ordre</th><th>Kunde</th><th>Status</th><th>Årsag</th><th className="text-right">Stk.</th><th>Kreditnota</th><th>Oprettet</th></tr></thead>
        <tbody>
          {list.data?.map((r) => (
            <tr key={r.id} className="cursor-pointer hover:bg-canvas" onClick={() => setOpen(r.id)}>
              <td className="font-medium">{r.number}</td>
              <td><Link to="/orders/$id" params={{ id: r.orderId }} className="text-primary hover:underline" onClick={(e) => e.stopPropagation()}>{r.orderNumber}</Link></td>
              <td>{r.customerName}</td>
              <td><Badge tone={rtTone(r.status)}>{rtStatusLabel[r.status]}</Badge></td>
              <td className="text-xs">{r.reason}</td>
              <td className="text-right tabular-nums">{r.lines.reduce((t, l) => t + l.quantity, 0)}</td>
              <td>{r.creditStatus === 'due' ? <Badge tone="amber">skal krediteres</Badge> : r.creditStatus === 'credited' ? <Badge tone="green">krediteret</Badge> : ''}</td>
              <td className="whitespace-nowrap text-xs text-muted">{r.createdBy} · {fmtTime(r.createdAt)}</td>
            </tr>
          ))}
          {list.data?.length === 0 && <tr><td colSpan={8} className="text-muted">Ingen returer.</td></tr>}
        </tbody>
      </table>
      {open && <ReturnModal id={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

export function ReturnModal({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [error, setError] = useState<unknown>(null);
  const r = useQuery({ queryKey: ['return', id], queryFn: () => get<ReturnDto>(`/returns/${id}`) });
  const [qty, setQty] = useState<Record<string, { restock: string; scrap: string }>>({});
  const [loc, setLoc] = useState('');
  const set = (x: ReturnDto) => { qc.setQueryData(['return', id], x); void qc.invalidateQueries({ queryKey: ['returns'] }); setError(null); };
  const label = useMutation({ mutationFn: () => post<ReturnDto>(`/returns/${id}/label`), onSuccess: set, onError: setError });
  const receive = useMutation({ mutationFn: () => post<ReturnDto>(`/returns/${id}/receive`, { lines: r.data!.lines.map((l) => ({ lineId: l.id, restock: Number(qty[l.id]?.restock ?? 0) || 0, scrap: Number(qty[l.id]?.scrap ?? 0) || 0, locationId: loc || null })).filter((l) => l.restock + l.scrap > 0) }), onSuccess: set, onError: setError });
  const close = useMutation({ mutationFn: () => post<ReturnDto>(`/returns/${id}/close`), onSuccess: set, onError: setError });
  const cancel = useMutation({ mutationFn: () => post<ReturnDto>(`/returns/${id}/cancel`), onSuccess: set, onError: setError });
  const warehouses = useWarehouses();
  const d = r.data;
  const whId = warehouses.data?.find((w) => w.code === d?.warehouseCode)?.id ?? '';
  return (
    <Modal title={d ? `Retur ${d.number}` : 'Retur'} onClose={onClose}>
      {!d ? <Spinner /> : (
        <div className="space-y-3 text-sm">
          <div className="flex flex-wrap items-center gap-2"><Badge tone={rtTone(d.status)}>{rtStatusLabel[d.status]}</Badge><span>Ordre {d.orderNumber} · {d.customerName}</span>{d.reason && <span className="text-muted">· {d.reason}</span>}</div>
          <ErrorBox error={error} />
          <div className="flex flex-wrap gap-2">
            {d.status === 'open' && can('floor.returns') && <button className="btn" disabled={label.isPending} onClick={() => label.mutate()}>Lav GLS-returlabel og mail kunden</button>}
            {d.labelDocumentId && <a className="btn" href={`/api/documents/${d.labelDocumentId}/download`} target="_blank" rel="noreferrer">Returlabel (PDF)</a>}
            {d.status === 'received' && can('floor.returns') && <button className="btn" onClick={() => close.mutate()}>Luk</button>}
            {['open', 'labelled'].includes(d.status) && can('floor.returns') && <button className="btn" onClick={() => window.confirm('Annullér returen?') && cancel.mutate()}>Annullér</button>}
          </div>
          <table className="table">
            <thead><tr><th>SKU</th><th>Vare</th><th className="text-right">Retur</th><th className="text-right">Modtaget</th>{['open', 'labelled'].includes(d.status) && <><th>På lager</th><th>Kasseres</th></>}</tr></thead>
            <tbody>
              {d.lines.map((l) => (
                <tr key={l.id}>
                  <td>{l.sku}</td><td>{l.title}</td><td className="text-right tabular-nums">{l.quantity}</td>
                  <td className="text-right tabular-nums">{l.quantityReceived ? `${l.quantityRestocked} lager, ${l.quantityScrapped} kasseret` : 0}</td>
                  {['open', 'labelled'].includes(d.status) && (
                    <>
                      <td><input className="input w-16" type="number" min={0} value={qty[l.id]?.restock ?? ''} onChange={(e) => setQty({ ...qty, [l.id]: { restock: e.target.value, scrap: qty[l.id]?.scrap ?? '' } })} /></td>
                      <td><input className="input w-16" type="number" min={0} value={qty[l.id]?.scrap ?? ''} onChange={(e) => setQty({ ...qty, [l.id]: { scrap: e.target.value, restock: qty[l.id]?.restock ?? '' } })} /></td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {['open', 'labelled'].includes(d.status) && can('floor.returns') && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-muted">Læg på</span>
              {whId && <LocationInput warehouseId={whId} value={loc} onChange={(v) => setLoc(v)} placeholder="tom = modtagelse" />}
              <button className="btn btn-primary" disabled={receive.isPending} onClick={() => receive.mutate()}>Modtag retur</button>
            </div>
          )}
          {d.creditStatus === 'due' && <div className="text-muted">Ordren er faktureret eller kortbetalt; kreditnotaen laves under Økonomi.</div>}
        </div>
      )}
    </Modal>
  );
}

/** "Opret retur" on the order page: quantities per shipped line, optional GLS return label. */
export function CreateReturnButton({ order }: { order: OrderDto }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState<Record<string, string>>({});
  const [reason, setReason] = useState('');
  const [label, setLabel] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [created, setCreated] = useState<string | null>(null);
  const shipped = order.lines.filter((l) => l.fulfilledQty > 0);
  const create = useMutation({
    mutationFn: () => post<ReturnDto>('/returns', { orderId: order.id, reason: reason || null, label, lines: shipped.filter((l) => Number(qty[l.id]) > 0).map((l) => ({ orderLineId: l.id, quantity: Number(qty[l.id]) })) }),
    onSuccess: (r) => { setOpen(false); setCreated(r.id); void qc.invalidateQueries({ queryKey: ['returns'] }); void qc.invalidateQueries({ queryKey: ['order', order.id] }); },
    onError: setError,
  });
  if (!can('floor.returns') || !shipped.length) return null;
  return (
    <>
      <button className="btn" onClick={() => setOpen(true)}>Opret retur</button>
      {created && <ReturnModal id={created} onClose={() => setCreated(null)} />}
      {open && (
        <Modal title={`Retur på ordre ${order.externalNumber ?? order.number}`} onClose={() => setOpen(false)}>
          <div className="space-y-3 text-sm">
            <ErrorBox error={error} />
            <table className="table">
              <thead><tr><th>SKU</th><th>Vare</th><th className="text-right">Sendt</th><th>Retur</th></tr></thead>
              <tbody>{shipped.map((l) => <tr key={l.id}><td>{l.sku}</td><td>{l.title}</td><td className="text-right tabular-nums">{l.fulfilledQty}</td><td><input className="input w-16" type="number" min={0} max={l.fulfilledQty} value={qty[l.id] ?? ''} onChange={(e) => setQty({ ...qty, [l.id]: e.target.value })} /></td></tr>)}</tbody>
            </table>
            <div><label className="label">Årsag</label><input className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="fx fortrudt køb, defekt, forkert vare" /></div>
            <label className="flex items-center gap-2"><input type="checkbox" checked={label} onChange={(e) => setLabel(e.target.checked)} /> Lav GLS-returlabel og mail den til kunden</label>
            <div className="flex justify-end gap-2"><button className="btn" onClick={() => setOpen(false)}>Annullér</button><button className="btn btn-primary" disabled={create.isPending || !shipped.some((l) => Number(qty[l.id]) > 0)} onClick={() => create.mutate()}>Opret retur</button></div>
          </div>
        </Modal>
      )}
    </>
  );
}

